import * as React from "react";
import * as Redux from "redux";
import axios from "axios";
import { AppDispatch, IAppStore } from "app/store";
import { connect } from "react-redux";
import {
  withRouter,
  RouteComponentProps,
  match as Match,
} from "react-router-dom";
// interface
import { IAppState } from "app/rootReducer";
// components
import InterestsComponent from "./components";
// actions
import {
  fetchingUserProfile,
  getUserInterests,
  toggleInterestToPrivate,
  setInterestToDelete,
  toggleInterestBoost,
  setInterestToTop,
  setInterestToNormal,
  removeInterestFromTopInterests,
  swapTopInterestOrder,
  ActionCreators as ProfileActionCreators,
} from "@vingle/wtf-sdk/actions/userProfile";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { profileUserSelector } from "@vingle/wtf-sdk/selectors/userProfile";

function mapStateToProps(state: IAppState) {
  return {
    currentUser: state.app.currentUser,
    userProfile: {
      ...state.userProfile,
      profile: {
        ...state.userProfile.profile,
        profile: profileUserSelector(state),
      },
    },
  };
}

function mapDispatchToProps(dispatch: AppDispatch) {
  return Redux.bindActionCreators(
    {
      fetchingUserProfile,
      getUserInterests,
      toggleInterestBoost,
      openInterestMenu: ProfileActionCreators.openInterestMenu,
      closeInterestMenu: ProfileActionCreators.closeInterestMenu,
      toggleInterestToPrivate,
      setInterestToDelete,
      openChangeTopInterestsDialog:
        ProfileActionCreators.openChangeTopInterestsDialog,
      setInterestToTop,
      setInterestToNormal,
      closeChangeTopInterestsDialog:
        ProfileActionCreators.closeChangeTopInterestsDialog,
      selectTopInterest: ProfileActionCreators.selectTopInterest,
      removeInterestFromTopInterests,
      swapTopInterestOrder,
    },
    dispatch,
  );
}

const DEFAULT_INCREASE_VALUE = 10000; // same with Android

interface IProps
  extends RouteComponentProps<Vingle.IMatchParams>,
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {}

class InterestsContainer extends React.PureComponent<IProps> {
  private cancelTokenSource = axios.CancelToken.source();
  public componentDidMount() {
    const { match } = this.props;
    if (match.params.username) {
      this.loadProfileData(match.params.username);
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    const { match } = this.props;
    if (
      match.params.username &&
      match.params.username !== prevProps.match.params.username
    ) {
      this.cancelTokenSource.cancel();
      this.cancelTokenSource = axios.CancelToken.source();
      this.loadProfileData(match.params.username);
    }
  }

  public componentWillUnmount() {
    this.cancelTokenSource.cancel();
  }

  public render() {
    const { currentUser, userProfile } = this.props;
    return (
      <InterestsComponent
        currentUser={currentUser}
        user={userProfile.profile.profile}
        userProfile={userProfile}
        canEdit={this.canEdit()}
        onBackToInterestsPage={this.handleBackToInterestsPage}
        sortOutInterests={this.sortOutInterests()}
        onChangeTopInterestOrder={this.handleChangeTopInterestOrder}
        onBoost={this.handleBoost}
        onInterestToNormal={this.handleInterestToNormal}
        openInterestPopoverMenu={this.openInterestPopoverMenu}
        onInterestToTop={this.handleInterestToTop}
        closeInterestPopoverMenu={this.closeInterestPopoverMenu}
        onToggleInterestPrivate={this.handleToggleInterestPrivate}
        onDeleteInterest={this.handleDeleteInterest}
        onEditDone={this.handleEditDone}
        onTopInterestsDialogCloseRequest={
          this.handleTopInterestsDialogCloseRequest
        }
        onTopInterestsDialogSelectChange={
          this.handleTopInterestsDialogSelectChange
        }
        onTopInterestsDialogRequestSubmit={
          this.handleTopInterestsDialogRequestSubmit
        }
      />
    );
  }

  private readonly loadProfileData = (username: string) => {
    const cancelToken = this.cancelTokenSource.token;
    this.props.fetchingUserProfile({ username });
    this.props.getUserInterests({
      username,
      cancelToken,
    });
  };

  private readonly canEdit = () => {
    return VingleURL.UserShowEditInterests.isSame(this.props.location.pathname);
  };

  private readonly handleEditDone = () => {
    this.handleBackToInterestsPage();
  };

  private readonly handleBackToInterestsPage = () => {
    const { match, history } = this.props;
    history.push(
      new VingleURL.UserShowInterests({
        username: String(match.params.username),
      }).toString(),
    );
  };

  private readonly handleBoost = (
    interest: Vingle.UserProfile.IProfileInterestState,
  ) => {
    const { match } = this.props;
    if (interest.data && match.params.username) {
      this.props.toggleInterestBoost(
        match.params.username,
        interest.data.interest.name,
        !interest.data.relation.boost,
      );
    }
  };

  private readonly openInterestPopoverMenu = (
    interest: Vingle.UserProfile.IProfileInterestState,
    targetAnchor: HTMLElement,
  ) => {
    this.props.openInterestMenu({ interest, targetAnchor });
  };

  private readonly closeInterestPopoverMenu = () => {
    this.props.closeInterestMenu();
  };

  private readonly handleToggleInterestPrivate = () => {
    const { userProfile, currentUser } = this.props;

    if (
      currentUser &&
      userProfile.interests.meta.selectedNormalInterest &&
      userProfile.interests.meta.selectedNormalInterest.data
    ) {
      const interest = userProfile.interests.meta.selectedNormalInterest.data;
      this.props.toggleInterestToPrivate(
        currentUser.username,
        interest.interest.name,
        !interest.private,
      );
      this.closeInterestPopoverMenu();
    }
  };

  private readonly handleDeleteInterest = () => {
    const { userProfile, currentUser } = this.props;
    if (
      currentUser &&
      userProfile.interests.meta.selectedNormalInterest &&
      userProfile.interests.meta.selectedNormalInterest.data
    ) {
      this.props.setInterestToDelete(
        currentUser.username,
        userProfile.interests.meta.selectedNormalInterest.data.interest.name,
      );
      this.closeInterestPopoverMenu();
    }
  };

  private readonly handleInterestToTop = (
    topInterest: Vingle.UserProfile.IProfileInterestState,
  ) => {
    const { currentUser, userProfile } = this.props;
    if (!userProfile.interests.interests || !currentUser) return;

    const topInterestsCount = userProfile.interests.interests.filter(
      (interest: Vingle.UserProfile.IProfileInterestState) => {
        if (interest && interest.data) {
          return interest.data.top > 0;
        }
        return false;
      },
    ).length;

    if (topInterestsCount >= 4) {
      this.props.openChangeTopInterestsDialog({
        interest: topInterest,
      });
    } else if (topInterest.data) {
      this.props.setInterestToTop(
        currentUser.username,
        topInterest.data.interest.name,
        (topInterestsCount + 1) * DEFAULT_INCREASE_VALUE,
      );
    }
  };

  private readonly handleInterestToNormal = (
    interest: Vingle.UserProfile.IProfileInterestState,
  ) => {
    const { currentUser } = this.props;
    if (interest.data && currentUser) {
      this.props.setInterestToNormal(
        currentUser.username,
        interest.data.interest.name,
      );
    }
  };

  private readonly handleTopInterestsDialogCloseRequest = () => {
    this.props.closeChangeTopInterestsDialog();
  };

  private readonly handleTopInterestsDialogSelectChange = (
    interest: Vingle.UserProfile.IProfileInterestState,
  ) => {
    this.props.selectTopInterest({ interest });
  };

  private readonly handleTopInterestsDialogRequestSubmit = async (
    username: string,
    interestToRemove: Vingle.UserProfile.IProfileInterestState,
  ) => {
    const { userProfile } = this.props;
    if (userProfile.interests.meta.selectedNormalInterest) {
      await this.props.removeInterestFromTopInterests(
        username,
        interestToRemove,
        userProfile.interests.meta.selectedNormalInterest,
      );
    }
  };

  private readonly handleChangeTopInterestOrder = (
    oldIndex: number,
    newIndex: number,
  ) => {
    const { userProfile } = this.props;
    const { topInterests } = this.sortOutInterests();
    const username = userProfile.profile.profile
      ? userProfile.profile.profile.username
      : "";

    if (
      oldIndex === newIndex ||
      !topInterests[oldIndex].data ||
      !topInterests[newIndex].data
    ) {
      return;
    }

    const originalInterest = (topInterests[oldIndex]
      .data as Vingle.UserProfile.IUserInterest).interest.name;
    const originalTop = (topInterests[oldIndex]
      .data as Vingle.UserProfile.IUserInterest).top;
    const newInterest = (topInterests[newIndex]
      .data as Vingle.UserProfile.IUserInterest).interest.name;
    const newTop = (topInterests[newIndex]
      .data as Vingle.UserProfile.IUserInterest).top;

    this.props.swapTopInterestOrder({
      username,
      originalInterest,
      originalTop,
      newInterest,
      newTop,
    });
  };

  private readonly sortOutInterests = () => {
    const { userProfile } = this.props;
    const topInterests: Vingle.UserProfile.IProfileInterestState[] = [];
    const notTopInterests: Vingle.UserProfile.IProfileInterestState[] = [];

    userProfile.interests.interests.map(
      (interest: Vingle.UserProfile.IProfileInterestState) => {
        if (interest.data && interest.data.top > 0) {
          topInterests.push(interest);
        } else {
          notTopInterests.push(interest);
        }
      },
    );

    topInterests.sort(
      (
        left: Vingle.UserProfile.IProfileInterestState,
        right: Vingle.UserProfile.IProfileInterestState,
      ) => {
        if (left && left.data && (right && right.data)) {
          return left.data.top - right.data.top;
        }
        return 0;
      },
    );

    return {
      topInterests,
      notTopInterests,
    };
  };
}

export function getInitialData({
  appStore,
  match,
}: {
  match: Match<Vingle.IMatchParams>;
  appStore: IAppStore;
  requestUrl: string;
}) {
  const username = match.params.username;
  if (username) {
    const cancelToken = axios.CancelToken.source().token;
    return Promise.all([
      appStore.dispatch(fetchingUserProfile({ username })),
      appStore.dispatch(getUserInterests({ username, cancelToken })),
    ]);
  }
  return Promise.resolve();
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(InterestsContainer),
);
